import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { uploadSwClient } from "@/features/file-management/pages/FilePage/uploadManager/UploadServiceWorkerClient";
import filesService from "@/features/file-management/pages/FilePage/services/files.service";
import { workflowAnalyticActions } from "@/features/workflow/stores/workflowAnalyticSlice";
import type { UploadWorkerEvent } from "@/features/file-management/pages/FilePage/uploadManager/UploadServiceWorkerClient";
import { logger } from "@/logger";

/**
 * Custom hook to handle upload completion analytics.
 * After upload completes, fetches download URL and triggers workflow analytic pipe request.
 * 
 * Usage:
 * ```tsx
 * const MyComponent = () => {
 *   useUploadCompletionAnalytics();
 *   return <YourContent />;
 * };
 * ```
 */
export const useUploadCompletionAnalytics = (): void => {
  logger.debug("[useUploadCompletionAnalytics] init");
  const dispatch = useDispatch();
  

  useEffect(() => {
    if (!uploadSwClient) {
      logger.warn("[useUploadCompletionAnalytics] uploadSwClient not available");
      return;
    }

    // Subscribe to upload completion events
    const unsubscribe = uploadSwClient.subscribe((event: UploadWorkerEvent) => {
      logger.debug("[useUploadCompletionAnalytics] Received upload event", event);
      if (event.event  !== "completed" || !event.result) {
        return;
      }

      const item  = event.result;
      
      // Extract upload record data from event extra (sent by UploadManager)
      const uploadRecordExtra = event.uploadRecord;
      
      const record = {
        fileId: uploadRecordExtra?.backendFileId || item.fileId || "",
        id: uploadRecordExtra?.id || "", // document ID from item
        name: uploadRecordExtra?.name || item.fileName || "",
        sizeBytes: uploadRecordExtra?.size || item.size || 0,
        cloudLink: uploadRecordExtra?.cloudLink || item.cloudLink || "", // Use cloudLink from uploadRecordExtra or item
        bucketName: uploadRecordExtra?.bucketName || item.bucketName || "", // Would need to be stored/returned from API
        type: uploadRecordExtra?.type,
        version: uploadRecordExtra?.version || item.versionResult || "1",
        folderId: uploadRecordExtra?.folderId,
        relativePath: uploadRecordExtra?.relativePath,
        isOrganization: uploadRecordExtra?.isOrg || item.isOrganization,
        backendFileId: uploadRecordExtra?.backendFileId,
        companyId: uploadRecordExtra?.companyId,
        project: uploadRecordExtra?.project,
      };

      // Early return if missing critical data
      if (!record.fileId) {
        logger.warn("[useUploadCompletionAnalytics] Missing fileId in completion event", { item });
        return;
      }

      const userId = getUserId();

      // Fetch download URL
      filesService.getDownloadUrl(record.cloudLink, record.bucketName, record.id).subscribe({
        next: (res) => {
          const downloadUrl = res?.url || "";

          // Build workflow request for analytics
          const workflowRequest = {
            event_type: "file.convert",
            context: {
              fileId: String(record.fileId),
              document_id: String(record.id),
              file_name: record.name,
              file_size: record.sizeBytes,
              mime_type: record.type,
              title: "Document Convert Workflow",
            },
            payload: {
              filename: record.name,
              baseFileId: String(record.fileId),
              baseMajorRev: 0,
              baseMinorRev: 0,
              originalFilePath: record.cloudLink || ".",
              convertOptions: {
                convert3DModel: 1,
                convert2DSheet: 1,
                extractProperties: 1,
                childModels: 0,
              },
              company_id: String(record.companyId || localStorage.getItem("companyId") || ""),
              project_id: String(record.project || localStorage.getItem("projectId") || ""),
              mime_type: record.type,
              file_id: String(record.fileId),
              version: String(record.version || "1"),
              document_id: String(record.id),
              file_name: record.name,
              file_path: record.cloudLink,
              bucket_name: record.bucketName,
              download_url: downloadUrl,
            },
            user_id: userId,
          };

          logger.debug("[useUploadCompletionAnalytics] Triggering analytics workflow", {
            fileId: record.fileId,
            documentId: record.id,
            downloadUrl,
          });

          // Dispatch workflow analytics request
          dispatch(workflowAnalyticActions.triggerAnalyticsPipeRequest(workflowRequest));
        },
        error: (error) => {
          logger.error(
            "[useUploadCompletionAnalytics] Failed to get DownloadUrl",
            {
              fileName: record.name,
              fileId: record.fileId,
              error: error instanceof Error ? error.message : String(error),
            }
          );
        },
      });
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [dispatch]);
};

/**
 * Helper function to get userId from localStorage.
 * Supports multiple storage formats.
 */
function getUserId(): string {
  // Try from user profile
  const userProfileStr = localStorage.getItem("user.profile");
  if (userProfileStr) {
    try {
      const profile = JSON.parse(userProfileStr);
      if (profile.Id) return String(profile.Id);
    } catch {
      // Fallback to next method
    }
  }

  // Try direct userID field
  const userID = localStorage.getItem("userID");
  if (userID) return userID;

  // Try userRootId field
  const userRootId = localStorage.getItem("userRootId");
  if (userRootId) return userRootId;

  return "";
}
